import { useEffect } from 'react';
import { Button as Btn } from 'phoenix-components';
import styles from 'styles/shop/product.module.css';
import { useLanguageLoader } from 'contexts/language';
import Loader from 'ui/services/loader';
import Api from 'ui/api';
import { SnackBar } from 'ui/services';
import { useField, useFormikContext } from 'formik';
import { useRefreshLocalCart } from 'contexts/cart';
import { useIsCustomDomain, useIsVisitor, useRefreshProfile, useShowLogin } from 'contexts/profile';
import ShopUtils from 'ui/lib/shopUtils';
import { useRouter } from 'next/router';
import { useToggle } from 'hooks/common';
import Alert from 'components/alert/Alert';
import { useProductContext } from 'components/products/context';
import { useSelectedVariant } from 'components/products/hooks';
import { AVAILABLE_TYPE } from 'components/products/utils';
import { useQueryParams } from 'hooks/location';


export function BuyButton() {
  const lang = useLanguageLoader();
  const {
    values,
    submitForm,
  } = useFormikContext();
  const {
    product,
    shop
  } = useProductContext();
  const selectedVariant = useSelectedVariant();
  const refreshCart = useRefreshLocalCart();
  const refreshProfile = useRefreshProfile();
  const isVisitor = useIsVisitor();
  const showLogin = useShowLogin(shop, isVisitor ? 'addToCart' : '');
  const isCustomDomain = useIsCustomDomain();
  const router = useRouter();
  const params = useQueryParams();
  const [, , { setValue: setQuantity }] = useField('quantity');
  const [showSellerInfo, toggleShowSellerInfo] = useToggle();
  const [shopError, toggleShopError] = useToggle();
  const [productError, toggleProductError] = useToggle();

  const isDirectLogin = ShopUtils.isDirectCheckout(shop);

  useEffect(() => {
    if(params.has('addToCart') && !isVisitor){
      const type = params.get('addToCart');
      params.delete('addToCart');
      router.replace(
        {
          search: params.toString(),
        },
        null,
        {
          shallow: true,
        }
      );
      
      handleCart(type)();
    }
  }, [isVisitor]);

  const handleCart = (type) => async () => {
    const minQuantity = product?.quantity?.minQuantity ?? 1;
    const quantity = values?.quantity;
    const variant = selectedVariant;
    if(variant?.availableType === AVAILABLE_TYPE.FINITE && variant?.available < quantity){
      let errTxt = variant?.available ? 
        `Sorry, we only have ${variant?.available}` 
        : 'Sorry, we are out of stock';
      SnackBar.showError(errTxt);
      return;
    }
    if (values?.quantity < minQuantity) {
      setQuantity(minQuantity);
      SnackBar.showError(`Minimum Order Quantity should be ${minQuantity}`);
      return;
    }
    if (shop?.status === 'unlive') {
      toggleShopError();
      return;
    }
    if (product.status !== 'live' && product.status !== 'created') {
      toggleProductError();
      return;
    }
    if (!product.enablePay) {
      toggleShowSellerInfo();
      return;
    }

    if (isVisitor) {
      if (isDirectLogin) {
        try {
          Loader.show();
          await Api.loginAsGuest();
          await refreshProfile();
        } catch (e) {
          SnackBar.showError(e);
          return true;
        } finally {
          Loader.hide();
        }
      }
      params.set('addToCart', type);
      router.push({
        search : params.toString()
      });
      showLogin();
      return true;
    }
    try {
      Loader.show();
      await submitForm();
      SnackBar.show('Added to cart');
      if (type !== 'cart') {
        router.push({
          pathname: isCustomDomain ? '/cart' : `/${shop?.slug}/cart`,
        });
      } else {
        refreshCart();
      }
    } catch (e) {
      SnackBar.showError(e);
    } finally {
      if(params.has('type')){
        params.delete('type');
        history.push();
      }
      Loader.hide();

    }
  };

  return (
    <>
      {showSellerInfo && (
        <Alert
          onClose={toggleShowSellerInfo}
          text={'Sorry, payments are not enabled for this seller. '
            + 'If you\'re a seller, please connect your account details and enable the payments.'}/>
      )}
      {shopError && (
        <Alert
          onClose={toggleShopError}
          text="Sorry, Shop is unlive. Please contact seller"
        />
      )}
      {productError && (
        <Alert
          onClose={toggleProductError}
          text={`Sorry, Product is ${product?.status}. Please contact seller`}
        />
      )}
      <div className={styles.buttonContainer}>
        <Btn
          fullWidth
          primary={false}
          className={styles.a2c}
          onClick={handleCart('cart')}
          size="large"
          label={lang('buttons.addToCart')}
        />
        <Btn
          fullWidth
          className={styles.buyNow}
          onClick={handleCart('submit')}
          size="large"
          label={lang('buttons.buyNow')}
        />
      </div>
    </>
  );
}

BuyButton.propTypes = {};
